import { useEffect, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- dashboard reload is intentionally keyed by branch */
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { calcularRiesgosFuga, type RiesgoFuga } from '../../utils/riesgoFuga';
import QRCode from 'qrcode';
import RegistrarPagoModal from '../pagos/components/RegistrarPagoModal';

type VencimientoUsuario = {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  tipo_membresia: string;
  fecha_vencimiento: string;
  estado: string;
};

type ReservaHoy = {
  id: string;
  time: string;
  reserved: number;
  capacity: number;
  percentage: number;
};

type PlanItem = {
  name: string;
  value: number;
  color: string;
};

type Activity = {
  id: string;
  type: 'reservation' | 'payment' | 'user';
  user: string;
  action: string;
  time: string;
};

type UsuarioDentro = {
  id: string;
  nombre: string;
  entradaAt: string | null;
};

type CamaraEntrada = {
  id: string;
  nombre: string;
  estado_conexion: string;
  ultimo_contacto_at: string | null;
  metadata: { manual_open_supported?: boolean } | null;
};

const PLAN_COLORS = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'];

const getTimeAgo = (isoDate: string) => {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffMinutes < 1) return 'Hace instantes';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Hace ${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays} día${diffDays === 1 ? '' : 's'}`;
};

export default function HomePage() {
  const { profile } = useAuth();
  const isStaff = profile?.role === 'staff';
  const puedeVerRiesgo = ['platform_admin', 'gym_admin', 'admin'].includes(profile?.role || '');
  const puedeAbrirTorniquete = ['platform_admin', 'gym_admin', 'admin'].includes(profile?.role || '')
    || (profile?.role === 'staff' && ['general', 'recepcionista'].includes(profile?.tipoStaff || ''));
  const { sucursalActiva, loading: loadingSucursal } = useSucursal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalUsuarios: 0,
    usuariosActivos: 0,
    reservasHoy: 0,
  });
  const [vencimientos, setVencimientos] = useState<VencimientoUsuario[]>([]);
  const [reservasHoy, setReservasHoy] = useState<ReservaHoy[]>([]);
  const [planDistribution, setPlanDistribution] = useState<PlanItem[]>([]);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [aforoActual, setAforoActual] = useState(0);
  const [usuariosDentro, setUsuariosDentro] = useState<UsuarioDentro[]>([]);
  const [mostrarUsuariosDentro, setMostrarUsuariosDentro] = useState(false);
  const [marcandoSalidaId, setMarcandoSalidaId] = useState<string | null>(null);
  const [riesgosFuga, setRiesgosFuga] = useState<RiesgoFuga[]>([]);
  const [mostrarRiesgos, setMostrarRiesgos] = useState(false);
  const [mostrarQrInscripcion, setMostrarQrInscripcion] = useState(false);
  const [qrInscripcion, setQrInscripcion] = useState('');
  const [urlInscripcion, setUrlInscripcion] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [mostrarRegistrarPago, setMostrarRegistrarPago] = useState(false);
  const [metodosPago, setMetodosPago] = useState<Array<{ value: string; label: string }>>([]);
  const [mostrarAperturaManual, setMostrarAperturaManual] = useState(false);
  const [camarasEntrada, setCamarasEntrada] = useState<CamaraEntrada[]>([]);
  const [camaraEntradaId, setCamaraEntradaId] = useState('');
  const [motivoApertura, setMotivoApertura] = useState('Acceso autorizado por recepción');
  const [cargandoCamaras, setCargandoCamaras] = useState(false);
  const [abriendoTorniquete, setAbriendoTorniquete] = useState(false);
  const [resultadoApertura, setResultadoApertura] = useState<{ ok: boolean; mensaje: string } | null>(null);

  const abrirModalApertura = async () => {
    if (!sucursalActiva?.id) return;
    setMostrarAperturaManual(true);
    setCargandoCamaras(true);
    setResultadoApertura(null);
    const { data, error: cameraError } = await supabase
      .from('control_acceso_camaras')
      .select('id,nombre,estado_conexion,ultimo_contacto_at,metadata')
      .eq('sucursal_id', sucursalActiva.id)
      .eq('tipo', 'entrada')
      .eq('activa', true)
      .not('conector_id', 'is', null)
      .order('nombre');
    setCargandoCamaras(false);
    if (cameraError) {
      setCamarasEntrada([]);
      setResultadoApertura({ ok: false, mensaje: 'No se pudieron consultar los lectores de entrada.' });
      return;
    }
    const cameras = (data || []) as CamaraEntrada[];
    setCamarasEntrada(cameras);
    setCamaraEntradaId(cameras.find((camera) => camera.metadata?.manual_open_supported === true)?.id || cameras[0]?.id || '');
    if (!cameras.length) {
      setResultadoApertura({ ok: false, mensaje: 'Esta sede no tiene un lector de entrada configurado.' });
    }
  };

  const solicitarAperturaManual = async () => {
    const selectedCamera = camarasEntrada.find((camera) => camera.id === camaraEntradaId);
    if (!sucursalActiva?.id || !camaraEntradaId || selectedCamera?.metadata?.manual_open_supported !== true || motivoApertura.trim().length < 3) return;
    setAbriendoTorniquete(true);
    setResultadoApertura(null);
    const { data, error: requestError } = await supabase.rpc('control_acceso_solicitar_apertura_manual', {
      p_sucursal_id: sucursalActiva.id,
      p_camara_id: camaraEntradaId,
      p_motivo: motivoApertura.trim(),
    });
    if (requestError) {
      const knownMessage = requestError.message.includes('APERTURA_MANUAL_EN_CURSO')
        ? 'Ya hay una apertura en curso para este torniquete.'
        : requestError.message.includes('LECTOR_ENTRADA_NO_DISPONIBLE')
          ? 'El lector seleccionado ya no está disponible.'
          : 'No fue posible solicitar la apertura. Intenta nuevamente.';
      setResultadoApertura({ ok: false, mensaje: knownMessage });
      setAbriendoTorniquete(false);
      return;
    }

    const commandId = typeof data === 'object' && data !== null ? String((data as { id?: string }).id || '') : '';
    if (!commandId) {
      setResultadoApertura({ ok: false, mensaje: 'No se recibió la confirmación de la solicitud.' });
      setAbriendoTorniquete(false);
      return;
    }

    for (let attempt = 0; attempt < 30; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      const { data: command } = await supabase
        .from('control_acceso_aperturas_manuales')
        .select('estado,ultimo_error')
        .eq('id', commandId)
        .maybeSingle();
      if (command?.estado === 'completada') {
        setResultadoApertura({ ok: true, mensaje: 'Torniquete abierto correctamente.' });
        setAbriendoTorniquete(false);
        return;
      }
      if (['error', 'expirada'].includes(command?.estado || '')) {
        setResultadoApertura({
          ok: false,
          mensaje: command?.ultimo_error || 'El conector no pudo abrir el torniquete.',
        });
        setAbriendoTorniquete(false);
        return;
      }
    }

    setResultadoApertura({ ok: false, mensaje: 'El conector no respondió a tiempo. Revisa que esté encendido antes de reintentar.' });
    setAbriendoTorniquete(false);
  };

  const abrirRegistroPago = async () => {
    if (!sucursalActiva?.id || !sucursalActiva.gimnasio_id) return;

    const cargarConfiguracion = async (porSucursal: boolean) => {
      let query = supabase
        .from('pago_configuracion')
        .select('*')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .order('updated_at', { ascending: false })
        .limit(1);
      query = porSucursal
        ? query.eq('sucursal_id', sucursalActiva.id)
        : query.is('sucursal_id', null);
      return query;
    };

    const sucursal = await cargarConfiguracion(true);
    const gimnasio = !sucursal.data?.[0]
      ? await cargarConfiguracion(false)
      : null;
    const cfg = sucursal.data?.[0] || gimnasio?.data?.[0];
    const opciones: Array<{ value: string; label: string }> = [];
    if (cfg?.efectivo === true) opciones.push({ value: 'efectivo', label: 'Efectivo' });
    if (cfg?.tarjeta_credito === true || cfg?.tarjeta_debito === true) {
      opciones.push({ value: 'tarjeta', label: 'Tarjeta' });
    }
    if (cfg?.transferencia === true) {
      opciones.push({ value: 'transferencia', label: 'Transferencia' });
    }
    if (cfg?.otro === true) opciones.push({ value: 'otro', label: 'Otro' });
    setMetodosPago(opciones);
    setMostrarRegistrarPago(true);
  };

  useEffect(() => {
    if (!mostrarQrInscripcion || !sucursalActiva?.gimnasio_id) return;

    let activo = true;
    const prepararQr = async () => {
      const { data } = await supabase
        .from('gimnasios')
        .select('slug')
        .eq('id', sucursalActiva.gimnasio_id)
        .maybeSingle();
      const slug = data?.slug || 'mowe';
      const url = slug === 'mowe'
        ? 'https://app.mowechile.cl/inscripcion/mowe'
        : `${window.location.origin}/inscripcion/${encodeURIComponent(slug)}`;
      const imagen = await QRCode.toDataURL(url, {
        width: 720,
        margin: 2,
        color: { dark: '#172111', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
      if (activo) {
        setUrlInscripcion(url);
        setQrInscripcion(imagen);
      }
    };

    void prepararQr();
    return () => { activo = false; };
  }, [mostrarQrInscripcion, sucursalActiva?.gimnasio_id]);

  const copiarUrlInscripcion = async () => {
    await navigator.clipboard.writeText(urlInscripcion);
    setCopiado(true);
    window.setTimeout(() => setCopiado(false), 1800);
  };

  const descargarQrInscripcion = () => {
    if (!qrInscripcion) return;
    const enlace = document.createElement('a');
    enlace.href = qrInscripcion;
    enlace.download = 'qr-inscripcion-mowe.png';
    enlace.click();
  };

  const marcarSalidaManual = async (usuario: UsuarioDentro) => {
    if (!sucursalActiva?.id || marcandoSalidaId) return;
    const confirmado = window.confirm(
      `¿Confirmas que ${usuario.nombre} ya salió del gimnasio?\n\nEsto descontará a la persona del aforo actual.`,
    );
    if (!confirmado) return;

    setMarcandoSalidaId(usuario.id);
    const { error: salidaError } = await supabase.rpc('control_acceso_marcar_salida_manual', {
      p_sucursal_id: sucursalActiva.id,
      p_usuario_id: usuario.id,
      p_motivo: 'Salida no registrada por cámara',
    });
    setMarcandoSalidaId(null);

    if (salidaError) {
      window.alert(`No se pudo registrar la salida: ${salidaError.message}`);
      return;
    }

    setUsuariosDentro((actuales) => actuales.filter((item) => item.id !== usuario.id));
    setAforoActual((actual) => Math.max(0, actual - 1));
  };

  useEffect(() => {
    if (!loadingSucursal && sucursalActiva?.id) {
      cargarDashboard();
    }
  }, [loadingSucursal, sucursalActiva?.id]);

  useEffect(() => {
    if (!sucursalActiva?.id) {
      setAforoActual(0);
      setUsuariosDentro([]);
      return;
    }

    const cargarAforo = async () => {
      const { data, error: aforoError } = await supabase.rpc(
        'ocupacion_comercial_detalle',
        { p_sucursal_id: sucursalActiva.id },
      );

      if (aforoError) {
        console.error('Error cargando aforo:', aforoError);
        return;
      }

      const personas = (data || []).map((presencia: any) => ({
        id: presencia.usuario_id,
        nombre: presencia.nombre || 'Usuario sin nombre',
        entradaAt: presencia.entrada_at || null,
      }));
      setUsuariosDentro(personas);
      setAforoActual(personas.length);
    };

    cargarAforo();
    const intervalId = window.setInterval(cargarAforo, 10000);
    return () => window.clearInterval(intervalId);
  }, [sucursalActiva?.id]);

  const cargarDashboard = async () => {
    if (!sucursalActiva?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const hoy = new Date();
      const hoyStr = hoy.toISOString().split('T')[0];
      const diaSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][hoy.getDay()];
      const en30dias = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const hace28Dias = new Date(hoy.getTime() - 28 * 24 * 60 * 60 * 1000).toISOString();
      const [usuariosRes, horariosRes, reservasRes, accesosRes] = await Promise.all([
        isStaff
          ? Promise.resolve({ data: [], error: null, count: 0 })
          : supabase
              .from('usuarios')
              .select('id, nombre, estado, tipo_membresia, fecha_vencimiento, email, telefono, created_at', { count: 'exact' })
              .eq('sucursal_id', sucursalActiva.id),
        supabase
          .from('horarios')
          .select('id, hora_inicio, hora_fin, capacidad_maxima')
          .eq('sucursal_id', sucursalActiva.id)
          .eq('activo', true)
          .eq('dia_semana', diaSemana),
        supabase
          .from('reservas')
          .select('id, horario_id, usuario_id, estado, created_at')
          .eq('fecha_reserva', hoyStr)
          .in('estado', ['confirmada', 'pendiente']),
        puedeVerRiesgo
          ? supabase
              .from('control_acceso_eventos')
              .select('usuario_id, ocurrido_at')
              .eq('sucursal_id', sucursalActiva.id)
              .eq('tipo', 'entrada')
              .eq('autorizado', true)
              .not('usuario_id', 'is', null)
              .gte('ocurrido_at', hace28Dias)
              .order('ocurrido_at', { ascending: false })
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (usuariosRes.error) throw usuariosRes.error;
      if (horariosRes.error) throw horariosRes.error;
      if (reservasRes.error) throw reservasRes.error;
      if (accesosRes.error) throw accesosRes.error;

      const usuarios = usuariosRes.data || [];
      const horarios = horariosRes.data || [];
      const reservas = (reservasRes.data || []).filter((r) => horarios.some((h) => h.id === r.horario_id));

      const usuariosActivos = usuarios.filter((u) => u.estado === 'activo').length;
      setStats({
        totalUsuarios: usuariosRes.count || 0,
        usuariosActivos,
        reservasHoy: reservas.length,
      });

      const vencimientosActivos = usuarios
        .filter((u) => u.estado === 'activo' && u.fecha_vencimiento && u.fecha_vencimiento >= hoyStr && u.fecha_vencimiento <= en30dias)
        .sort((a, b) => new Date(a.fecha_vencimiento).getTime() - new Date(b.fecha_vencimiento).getTime())
        .slice(0, 8) as VencimientoUsuario[];
      setVencimientos(vencimientosActivos);

      const reservasPorHorario = horarios
        .map((h) => {
          const reserved = reservas.filter((r) => r.horario_id === h.id).length;
          const capacity = Math.max(1, Number(h.capacidad_maxima) || 1);
          const percentage = Math.round((reserved / capacity) * 100);

          return {
            id: h.id,
            time: `${h.hora_inicio?.slice(0, 5)} - ${h.hora_fin?.slice(0, 5)}`,
            reserved,
            capacity,
            percentage,
          };
        })
        .sort((a, b) => a.time.localeCompare(b.time))
        .slice(0, 8);
      setReservasHoy(reservasPorHorario);

      const planMap = new Map<string, number>();
      usuarios.forEach((u) => {
        const plan = u.tipo_membresia?.trim() || 'Sin plan';
        planMap.set(plan, (planMap.get(plan) || 0) + 1);
      });

      const plans = Array.from(planMap.entries())
        .map(([name, value], index) => ({
          name,
          value,
          color: PLAN_COLORS[index % PLAN_COLORS.length],
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);
      setPlanDistribution(plans);

      if (puedeVerRiesgo) {
        setRiesgosFuga(calcularRiesgosFuga(usuarios, accesosRes.data || [], hoy));
      } else {
        setRiesgosFuga([]);
      }

      const recentReservas = await supabase
        .from('reservas')
        .select('id, usuario_id, estado, created_at, horarios!inner(sucursal_id)')
        .eq('horarios.sucursal_id', sucursalActiva.id)
        .order('created_at', { ascending: false })
        .limit(10);

      const recentPagos = isStaff
        ? { data: [] as Array<{ id: string; usuario_id: string; monto: number; created_at: string }> }
        : await supabase
            .from('pagos')
            .select('id, usuario_id, monto, created_at')
            .eq('sucursal_id', sucursalActiva.id)
            .neq('modo', 'sandbox')
            .order('created_at', { ascending: false })
            .limit(10);

      const recentUsuarios = isStaff
        ? { data: [] as Array<{ id: string; nombre: string; created_at: string }> }
        : await supabase
            .from('usuarios')
            .select('id, nombre, created_at')
            .eq('sucursal_id', sucursalActiva.id)
            .order('created_at', { ascending: false })
            .limit(10);

      const userIds = new Set<string>();
      recentReservas.data?.forEach((r) => userIds.add(r.usuario_id));
      recentPagos.data?.forEach((p) => userIds.add(p.usuario_id));

      const usuariosMap = new Map<string, string>();
      if (userIds.size > 0) {
        const { data } = await supabase
          .from('usuarios')
          .select('id, nombre')
          .eq('sucursal_id', sucursalActiva.id)
          .in('id', Array.from(userIds));

        (data || []).forEach((u) => usuariosMap.set(u.id, u.nombre));
      }

      const activities: Activity[] = [
        ...(recentReservas.data || []).map((r) => ({
          id: `res-${r.id}`,
          type: 'reservation' as const,
          user: usuariosMap.get(r.usuario_id) || 'Usuario',
          action:
            r.estado === 'cancelada'
              ? 'canceló una reserva'
              : r.estado === 'confirmada'
              ? 'confirmó una reserva'
              : 'creó una reserva',
          time: getTimeAgo(r.created_at),
        })),
        ...(recentPagos.data || []).map((p) => ({
          id: `pay-${p.id}`,
          type: 'payment' as const,
          user: usuariosMap.get(p.usuario_id) || 'Usuario',
          action: `registró un pago de $${Number(p.monto).toLocaleString('es-CL')}`,
          time: getTimeAgo(p.created_at),
        })),
        ...(recentUsuarios.data || []).map((u) => ({
          id: `usr-${u.id}`,
          type: 'user' as const,
          user: u.nombre,
          action: 'se registró en el gimnasio',
          time: getTimeAgo(u.created_at),
        })),
      ]
        .sort((a, b) => {
          const aMinutes = Number(a.time.match(/\d+/)?.[0] || 0);
          const bMinutes = Number(b.time.match(/\d+/)?.[0] || 0);
          return aMinutes - bMinutes;
        })
        .slice(0, 10);

      setRecentActivities(activities);
    } catch (err: any) {
      console.error('Error cargando dashboard:', err);
      setError(err?.message || 'Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getDiasRestantes = (fechaVenc: string) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const venc = new Date(`${fechaVenc}T00:00:00`);
    return Math.ceil((venc.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getEstadoVencimiento = (dias: number) => {
    if (dias <= 3) return { label: 'Crítico', bg: 'bg-red-100', text: 'text-red-700' };
    if (dias <= 7) return { label: 'Urgente', bg: 'bg-orange-100', text: 'text-orange-700' };
    if (dias <= 14) return { label: 'Próximo', bg: 'bg-amber-100', text: 'text-amber-700' };
    return { label: 'Normal', bg: 'bg-teal-100', text: 'text-teal-700' };
  };

  if (loadingSucursal || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mb-4"></div>
            <p className="text-gray-600">Cargando dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!sucursalActiva) {
    return (
      <AdminLayout>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">Sin sucursal seleccionada</h3>
          <p className="text-amber-600">Selecciona una sucursal para continuar</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={cargarDashboard} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Reintentar
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Resumen operativo de {sucursalActiva.nombre}</p>
          </div>
        </div>

        <section aria-label="Acciones rápidas">
          <div className={`grid grid-cols-2 sm:grid-cols-4 ${isStaff ? 'xl:grid-cols-4' : 'xl:grid-cols-8'} gap-2.5 md:gap-3`}>
            {!isStaff && (
              <>
              <QuickAction to="/usuarios" icon="ri-user-add-line" label="Nuevo socio" color="teal" />
              <QuickAction onClick={() => setMostrarQrInscripcion(true)} icon="ri-qr-code-line" label="QR de inscripción" color="teal" />
              <QuickAction to="/usuarios" icon="ri-search-line" label="Buscar socio" color="blue" />
              </>
            )}
            <QuickAction onClick={() => void abrirRegistroPago()} icon="ri-bank-card-line" label="Registrar pago" color="green" />
            {puedeAbrirTorniquete && (
              <QuickAction onClick={() => void abrirModalApertura()} icon="ri-door-open-line" label="Acceso manual" color="orange" />
            )}
            {!isStaff && (
              <>
              <QuickAction to="/reservas" icon="ri-calendar-check-line" label="Reservas" color="purple" />
              <QuickAction to="/invitados" icon="ri-qr-scan-2-line" label="Invitados / QR" color="orange" />
              <QuickAction to="/notificaciones" icon="ri-notification-3-line" label="Notificaciones" color="slate" />
              </>
            )}
          </div>
        </section>

        {mostrarQrInscripcion && (
          <div className="fixed inset-0 z-[80] bg-black/55 flex items-center justify-center p-4" onClick={() => setMostrarQrInscripcion(false)}>
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Inscripción Mowe</h2>
                  <p className="text-sm text-gray-500 mt-1">Escanea el código para comenzar la inscripción.</p>
                </div>
                <button type="button" onClick={() => setMostrarQrInscripcion(false)} className="w-9 h-9 rounded-lg hover:bg-gray-100" aria-label="Cerrar">
                  <i className="ri-close-line text-xl text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                <div className="aspect-square max-w-72 mx-auto rounded-2xl border border-gray-200 p-3 flex items-center justify-center bg-white">
                  {qrInscripcion ? <img src={qrInscripcion} alt="QR de inscripción Mowe" className="w-full h-full" /> : <i className="ri-loader-4-line animate-spin text-4xl text-teal-700" />}
                </div>
                <p className="mt-5 px-3 py-2.5 rounded-lg bg-gray-50 text-xs text-gray-600 text-center break-all">{urlInscripcion || 'Preparando enlace…'}</p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button type="button" disabled={!urlInscripcion} onClick={() => void copiarUrlInscripcion()} className="px-4 py-3 rounded-xl border border-gray-300 font-semibold text-gray-700 disabled:opacity-50">
                    <i className={`${copiado ? 'ri-check-line' : 'ri-file-copy-line'} mr-2`} />{copiado ? 'Copiado' : 'Copiar enlace'}
                  </button>
                  <button type="button" disabled={!qrInscripcion} onClick={descargarQrInscripcion} className="px-4 py-3 rounded-xl bg-teal-700 text-white font-semibold disabled:opacity-50">
                    <i className="ri-download-2-line mr-2" />Descargar QR
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {mostrarRegistrarPago && (
          <RegistrarPagoModal
            onClose={() => setMostrarRegistrarPago(false)}
            onPagoRegistrado={() => void cargarDashboard()}
            metodosPagoDisponibles={metodosPago}
          />
        )}

        {mostrarAperturaManual && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/55 p-4" onClick={() => !abriendoTorniquete && setMostrarAperturaManual(false)}>
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Abrir torniquete</h2>
                  <p className="mt-1 text-sm text-gray-500">{sucursalActiva.nombre} · La acción quedará registrada.</p>
                </div>
                <button type="button" disabled={abriendoTorniquete} onClick={() => setMostrarAperturaManual(false)} className="grid h-9 w-9 place-items-center rounded-lg hover:bg-gray-100 disabled:opacity-40" aria-label="Cerrar">
                  <i className="ri-close-line text-xl text-gray-600" />
                </button>
              </div>
              <div className="space-y-4 p-5">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <i className="ri-alert-line mr-2" />Verifica visualmente a la persona antes de abrir.
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-gray-700">Torniquete de entrada</span>
                  <select value={camaraEntradaId} onChange={(event) => setCamaraEntradaId(event.target.value)} disabled={cargandoCamaras || abriendoTorniquete} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-gray-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 disabled:bg-gray-100">
                    {cargandoCamaras && <option value="">Consultando lectores…</option>}
                    {!cargandoCamaras && !camarasEntrada.length && <option value="">Sin lectores disponibles</option>}
                    {camarasEntrada.map((camera) => (
                      <option key={camera.id} value={camera.id}>{camera.nombre}{camera.metadata?.manual_open_supported !== true ? ' · Conector pendiente de actualizar' : camera.estado_conexion === 'en_linea' ? ' · En línea' : ' · Sin conexión confirmada'}</option>
                    ))}
                  </select>
                </label>
                {camaraEntradaId && camarasEntrada.find((camera) => camera.id === camaraEntradaId)?.metadata?.manual_open_supported !== true && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                    <i className="ri-information-line mr-2" />El servicio local de esta sede debe actualizarse antes de habilitar la apertura.
                  </div>
                )}
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-gray-700">Motivo</span>
                  <input value={motivoApertura} onChange={(event) => setMotivoApertura(event.target.value)} disabled={abriendoTorniquete} maxLength={180} className="w-full rounded-xl border border-gray-300 px-3 py-3 text-gray-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 disabled:bg-gray-100" />
                </label>
                {resultadoApertura && (
                  <div className={`rounded-xl border p-3 text-sm font-semibold ${resultadoApertura.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
                    <i className={`${resultadoApertura.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mr-2`} />{resultadoApertura.mensaje}
                  </div>
                )}
              </div>
              <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 p-5 sm:flex-row sm:justify-end">
                <button type="button" disabled={abriendoTorniquete} onClick={() => setMostrarAperturaManual(false)} className="rounded-xl border border-gray-300 px-5 py-3 font-semibold text-gray-700 disabled:opacity-40">Cancelar</button>
                <button type="button" disabled={abriendoTorniquete || !camaraEntradaId || camarasEntrada.find((camera) => camera.id === camaraEntradaId)?.metadata?.manual_open_supported !== true || motivoApertura.trim().length < 3} onClick={() => void solicitarAperturaManual()} className="rounded-xl bg-teal-700 px-5 py-3 font-bold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50">
                  {abriendoTorniquete ? <><i className="ri-loader-4-line mr-2 animate-spin" />Abriendo…</> : <><i className="ri-door-open-line mr-2" />Abrir ahora</>}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-2 ${isStaff ? 'lg:grid-cols-2' : puedeVerRiesgo ? 'md:grid-cols-3 xl:grid-cols-5' : 'md:grid-cols-2 xl:grid-cols-4'} gap-3 md:gap-6`}>
          {!isStaff && <MetricCard title="Total Usuarios" value={String(stats.totalUsuarios)} icon="ri-user-line" color="blue" />}
          {!isStaff && <MetricCard title="Usuarios Activos" value={String(stats.usuariosActivos)} icon="ri-user-star-line" color="teal" />}
          <MetricCard
            title="Personas dentro"
            value={String(aforoActual)}
            icon="ri-door-open-line"
            color="orange"
            onClick={() => setMostrarUsuariosDentro(true)}
          />
          <MetricCard title="Reservas Hoy" value={String(stats.reservasHoy)} icon="ri-calendar-check-line" color="purple" />
          {puedeVerRiesgo && <MetricCard
            title="Riesgo de fuga"
            value={String(riesgosFuga.length)}
            icon="ri-user-unfollow-line"
            color={riesgosFuga.some((item) => item.nivel === 'alto') ? 'red' : 'amber'}
            onClick={() => setMostrarRiesgos(true)}
          />}
        </div>

        {mostrarRiesgos && (
          <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setMostrarRiesgos(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={(event) => event.stopPropagation()}>
              <div className="p-5 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Socios con riesgo de fuga</h2>
                  <p className="text-sm text-gray-500 mt-1">Señales basadas en ingresos de los últimos 28 días, caída de frecuencia y vencimiento próximo.</p>
                </div>
                <button type="button" onClick={() => setMostrarRiesgos(false)} className="w-9 h-9 rounded-lg hover:bg-gray-100 shrink-0" aria-label="Cerrar">
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
              <div className="p-5 overflow-y-auto max-h-[calc(85vh-105px)]">
                {riesgosFuga.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <i className="ri-shield-check-line text-4xl text-teal-500"></i>
                    <p className="mt-2 font-medium text-gray-700">No hay socios con señales relevantes de fuga.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {riesgosFuga.map((riesgo) => (
                      <div key={riesgo.id} className="rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold shrink-0 ${riesgo.nivel === 'alto' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {riesgo.nombre.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-gray-900 truncate">{riesgo.nombre}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${riesgo.nivel === 'alto' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              Riesgo {riesgo.nivel} · {riesgo.puntaje}/100
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{riesgo.email} · {riesgo.plan}</p>
                          <p className="text-sm text-gray-700 mt-1">{riesgo.motivos.join(' · ')}</p>
                        </div>
                        <div className="text-left sm:text-right shrink-0">
                          <p className="text-xs text-gray-500">Última asistencia</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {riesgo.ultimaAsistencia ? new Date(riesgo.ultimaAsistencia).toLocaleDateString('es-CL') : 'Sin registro'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {mostrarUsuariosDentro && (
          <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setMostrarUsuariosDentro(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(event) => event.stopPropagation()}>
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Personas dentro</h2>
                  <p className="text-sm text-gray-500">{sucursalActiva.nombre} · {aforoActual} persona{aforoActual === 1 ? '' : 's'}</p>
                </div>
                <button type="button" onClick={() => setMostrarUsuariosDentro(false)} className="w-9 h-9 rounded-lg hover:bg-gray-100" aria-label="Cerrar">
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
              <div className="p-5 overflow-y-auto max-h-[calc(80vh-90px)]">
                {usuariosDentro.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <i className="ri-door-open-line text-4xl text-gray-300"></i>
                    <p className="mt-2">No hay personas registradas dentro.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {usuariosDentro.map((usuario) => (
                      <div key={usuario.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100">
                        <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-semibold">
                          {usuario.nombre.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{usuario.nombre}</p>
                          <p className="text-xs text-gray-500">
                            {usuario.entradaAt
                              ? `Ingreso: ${new Date(usuario.entradaAt).toLocaleString('es-CL')}`
                              : 'Hora de ingreso no disponible'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => marcarSalidaManual(usuario)}
                          disabled={marcandoSalidaId === usuario.id}
                          className="shrink-0 px-3 py-2 rounded-lg border border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-50 text-sm font-medium"
                        >
                          {marcandoSalidaId === usuario.id ? 'Marcando...' : 'Marcar salida'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={`${isStaff ? 'lg:col-span-3' : 'lg:col-span-2'} bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6`}>
            <h3 className="text-base font-semibold text-gray-900 mb-4">Reservas de Hoy</h3>
            <div className="space-y-4">
              {reservasHoy.length === 0 ? (
                <p className="text-sm text-gray-500">No hay horarios registrados para hoy.</p>
              ) : (
                reservasHoy.map((res) => (
                  <div key={res.id} className="flex items-center gap-2 md:gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-teal-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{res.time}</p>
                      <p className="text-xs text-gray-500">
                        {res.reserved}/{res.capacity} cupos reservados
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="hidden sm:block w-20 md:w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            res.percentage >= 100
                              ? 'bg-red-500'
                              : res.percentage >= 90
                              ? 'bg-orange-500'
                              : res.percentage >= 70
                              ? 'bg-teal-500'
                              : 'bg-teal-400'
                          }`}
                          style={{ width: `${Math.min(res.percentage, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-10 text-right">{res.percentage}%</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {!isStaff && <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Distribución de Planes</h3>
            <div className="space-y-4">
              {planDistribution.length === 0 ? (
                <p className="text-sm text-gray-500">Sin datos de planes.</p>
              ) : (
                planDistribution.map((plan) => (
                  <div key={plan.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700">{plan.name}</span>
                      <span className="text-sm font-semibold text-gray-900">{plan.value}</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(plan.value / Math.max(1, stats.totalUsuarios)) * 100}%`,
                          backgroundColor: plan.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Usuarios</span>
              <span className="text-lg font-bold text-gray-900">{stats.totalUsuarios}</span>
            </div>
          </div>}
        </div>

        {!isStaff && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Próximos Vencimientos</h3>
            <div className="space-y-3">
              {vencimientos.length === 0 ? (
                <p className="text-sm text-gray-500">No hay membresías por vencer en los próximos 30 días.</p>
              ) : (
                vencimientos.map((usuario) => {
                  const dias = getDiasRestantes(usuario.fecha_vencimiento);
                  const estado = getEstadoVencimiento(dias);
                  return (
                    <div key={usuario.id} className="flex items-center gap-2 md:gap-4 p-3 rounded-lg border border-gray-100">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-600">
                          {usuario.nombre?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{usuario.nombre}</p>
                        <p className="text-xs text-gray-500 truncate">{usuario.tipo_membresia || 'Sin plan'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {dias === 0 ? 'Hoy' : dias === 1 ? 'Mañana' : `${dias} días`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(`${usuario.fecha_vencimiento}T00:00:00`).toLocaleDateString('es-CL', {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${estado.bg} ${estado.text}`}>
                        {estado.label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
            <div className="space-y-4">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-gray-500">Sin actividad reciente.</p>
              ) : (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`text-sm ${
                          activity.type === 'payment'
                            ? 'ri-money-dollar-circle-line text-green-600'
                            : activity.type === 'user'
                            ? 'ri-user-add-line text-blue-600'
                            : 'ri-bookmark-line text-teal-600'
                        }`}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                      <p className="text-xs text-gray-500">{activity.action}</p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{activity.time}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>}
      </div>
    </AdminLayout>
  );
}

function MetricCard({
  title,
  value,
  icon,
  color,
  onClick,
}: {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'teal' | 'purple' | 'green' | 'orange' | 'red' | 'amber';
  onClick?: () => void;
}) {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    teal: 'bg-teal-100 text-teal-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm p-3 md:p-6 border border-gray-100 min-w-0 ${onClick ? 'cursor-pointer hover:border-orange-300 hover:shadow-md transition-all' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => { if (event.key === 'Enter' || event.key === ' ') onClick(); } : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-xl md:text-3xl font-bold text-gray-900 break-words">{value}</p>
        </div>
        <div className={`hidden sm:flex w-10 h-10 md:w-12 md:h-12 rounded-lg items-center justify-center shrink-0 ${colorMap[color]}`}>
          <i className={`${icon} text-xl md:text-2xl`}></i>
        </div>
      </div>
    </div>
  );
}

function QuickAction({
  to,
  onClick,
  icon,
  label,
  color,
}: {
  to?: string;
  onClick?: () => void;
  icon: string;
  label: string;
  color: 'teal' | 'blue' | 'green' | 'purple' | 'orange' | 'slate';
}) {
  const colorMap = {
    teal: 'bg-teal-50 text-teal-700 border-teal-100 hover:border-teal-300',
    blue: 'bg-blue-50 text-blue-700 border-blue-100 hover:border-blue-300',
    green: 'bg-green-50 text-green-700 border-green-100 hover:border-green-300',
    purple: 'bg-purple-50 text-purple-700 border-purple-100 hover:border-purple-300',
    orange: 'bg-orange-50 text-orange-700 border-orange-100 hover:border-orange-300',
    slate: 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400',
  };

  const content = <>
      <i className={`${icon} text-xl`} aria-hidden="true"></i>
      <span className="text-sm font-semibold leading-tight">{label}</span>
    </>;
  const className = `min-h-20 rounded-xl border p-3 flex flex-col text-left justify-between gap-2 transition-colors ${colorMap[color]}`;

  return to ? <Link to={to} className={className}>{content}</Link> : (
    <button type="button" onClick={onClick} className={className}>{content}</button>
  );
}
